import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, RefreshCw } from 'lucide-react';
import type { AuditLogEntry, KeepaFilters } from '@sourcito/schemas';
import { Badge, Button, Card } from '../design';
import { useAuditLogs } from '../lib/auditLogs';

type ActionFilter =
  | 'all'
  | 'nl_search'
  | 'discovery'
  | 'supplier'
  | 'outreach'
  | 'pricing_sheet'
  | 'keepa_template'
  | 'keepa_search_run'
  | 'keepa_schedule'
  | 'sp_api'
  | 'brand'
  | 'product_flag'
  | 'credential'
  | 'auth';

const ACTION_OPTIONS: Array<{ value: ActionFilter; label: string; prefix?: string }> = [
  { value: 'all', label: 'All actions' },
  { value: 'nl_search', label: 'Searches', prefix: 'nl_search' },
  { value: 'discovery', label: 'Discovery', prefix: 'discovery' },
  { value: 'supplier', label: 'Suppliers', prefix: 'supplier' },
  { value: 'outreach', label: 'Outreach', prefix: 'outreach' },
  { value: 'pricing_sheet', label: 'Pricing sheets', prefix: 'pricing_sheet' },
  // Phase 1.8 pill set — surfaces every audit prefix from Passes 1-4.
  { value: 'keepa_template', label: 'Keepa templates', prefix: 'keepa_template' },
  { value: 'keepa_search_run', label: 'Keepa runs', prefix: 'keepa_search_run' },
  { value: 'keepa_schedule', label: 'Schedules', prefix: 'keepa_schedule' },
  { value: 'sp_api', label: 'SP-API', prefix: 'sp_api' },
  // brand.* covers enrichment_started / _claimed / _enriched / _failed /
  // _skipped / enqueue_refused / decision_updated.
  { value: 'brand', label: 'Brands', prefix: 'brand' },
  // Phase 1.9 Pass 4 — indie auto-flag + /inbox triage prefix.
  { value: 'product_flag', label: 'Product flags', prefix: 'product_flag' },
  { value: 'credential', label: 'Credentials', prefix: 'credential' },
  { value: 'auth', label: 'Auth', prefix: 'auth' },
];

export function AuditLogsPage() {
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all');
  const selected = ACTION_OPTIONS.find((o) => o.value === actionFilter)!;
  const { data, isLoading, isError, error, refetch, isFetching } = useAuditLogs({
    action: selected.prefix,
    limit: 50,
  });

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link
              to="/search"
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ChevronLeft className="size-5" />
            </Link>
            <div>
              <h1 className="text-xl font-medium leading-tight">Audit log</h1>
              <p className="text-xs text-fg-tertiary">
                Append-only history of every search, credential change, and login. Last 50 rows.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-md bg-surface-elev border border-border-subtle p-0.5">
              {ACTION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setActionFilter(opt.value)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-xs transition-colors duration-fast ${
                    actionFilter === opt.value
                      ? 'bg-accent/[0.18] text-accent-fg'
                      : 'text-fg-secondary hover:text-fg-primary'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Refresh"
              onClick={() => refetch()}
              loading={isFetching}
            >
              <RefreshCw className="size-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-10">
        {isLoading && <p className="text-sm text-fg-secondary">Loading…</p>}
        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              Failed to load audit logs: {(error as Error).message}
            </p>
          </Card>
        )}

        {data && data.length === 0 && (
          <Card className="text-center py-10">
            <p className="text-sm text-fg-secondary">No audit log rows for this filter.</p>
          </Card>
        )}

        {data && data.length > 0 && (
          <div className="space-y-2">
            {data.map((row) => (
              <AuditLogRow key={row.id} row={row} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function AuditLogRow({ row }: { row: AuditLogEntry }) {
  const changes = (row.changes ?? null) as {
    nlQuery?: string | null;
    translatedFilters?: KeepaFilters;
    rationale?: string;
    resultCount?: number;
    keepaTotalResults?: number;
    keepaSelection?: Record<string, unknown>;
    estimatedKeepaTokens?: number;
    provider?: string;
    label?: string | null;
    ok?: boolean;
    // brand.* prefix
    brandName?: string;
    domain?: number;
    reason?: string;
    attempts?: number;
    terminal?: boolean;
    productCount?: number;
    asinsHydrated?: number;
    totalReviewCount?: number;
    sizeBucket?: string | null;
    tokensLeft?: number;
    floor?: number;
    sourceRunId?: string | null;
  } | null;
  const cost = (row.costMetadata ?? null) as {
    provider?: string;
    model?: string;
    promptTokens?: number;
    completionTokens?: number;
    keepaTokensConsumed?: number;
    keepaTokensLeft?: number;
  } | null;

  const isSearch = row.action === 'product.discovered' || row.action === 'nl_search.preview';
  const isBrand = row.action.startsWith('brand.');
  const dt = new Date(row.occurredAt);

  return (
    <Card className={row.result === 'failure' ? 'border-negative/30' : ''}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-xs font-mono text-fg-tertiary tabular-nums">
            {dt.toLocaleString()}
          </span>
          <Badge
            variant={row.result === 'failure' ? 'negative' : 'neutral'}
            className="text-[10px]"
          >
            {row.action}
          </Badge>
          {row.result === 'failure' && (
            <Badge variant="negative" className="text-[10px]">
              failure
            </Badge>
          )}
        </div>
        {row.userEmail && (
          <span className="text-xs text-fg-tertiary truncate">{row.userEmail}</span>
        )}
      </div>

      {isSearch && changes && (
        <div className="space-y-1.5">
          {changes.nlQuery && (
            <p className="text-sm text-fg-primary">
              <span className="text-fg-tertiary text-xs uppercase tracking-wider mr-2">Query</span>
              <span className="italic">"{changes.nlQuery}"</span>
            </p>
          )}
          {changes.rationale && (
            <p className="text-sm text-fg-secondary">
              <span className="text-fg-tertiary text-xs uppercase tracking-wider mr-2">
                Rationale
              </span>
              {changes.rationale}
            </p>
          )}
          <div className="flex items-center gap-3 flex-wrap text-xs text-fg-tertiary tabular-nums">
            {changes.resultCount !== undefined && (
              <span>
                <span className="font-medium text-fg-secondary">{changes.resultCount}</span>{' '}
                products returned
              </span>
            )}
            {changes.keepaTotalResults !== undefined && (
              <span>
                Keepa total:{' '}
                <span className="font-medium text-fg-secondary">{changes.keepaTotalResults}</span>
              </span>
            )}
            {changes.estimatedKeepaTokens !== undefined && (
              <span>
                Est. ~
                <span className="font-medium text-fg-secondary">
                  {changes.estimatedKeepaTokens}
                </span>{' '}
                Keepa tokens
              </span>
            )}
            {cost?.keepaTokensConsumed !== undefined && (
              <span>
                Used:{' '}
                <span className="font-medium text-fg-secondary">{cost.keepaTokensConsumed}</span>{' '}
                Keepa
              </span>
            )}
            {cost?.keepaTokensLeft !== undefined && (
              <span>
                After: <span className="font-medium text-fg-secondary">{cost.keepaTokensLeft}</span>{' '}
                left
              </span>
            )}
            {cost?.model && (
              <span>
                AI: <span className="font-mono text-fg-secondary">{cost.model}</span> (
                {cost.promptTokens ?? 0}p / {cost.completionTokens ?? 0}c)
              </span>
            )}
          </div>
          {changes.translatedFilters && (
            <details className="mt-1.5">
              <summary className="text-xs text-fg-tertiary cursor-pointer hover:text-fg-secondary">
                Show filters JSON (AI output)
              </summary>
              <pre className="text-[11px] font-mono bg-surface-elev rounded-xs p-2 mt-1 overflow-x-auto">
                {JSON.stringify(changes.translatedFilters, null, 2)}
              </pre>
            </details>
          )}
          {changes.keepaSelection && (
            <details className="mt-1.5">
              <summary className="text-xs text-fg-tertiary cursor-pointer hover:text-fg-secondary">
                Show raw Keepa /query selection (what we sent over the wire)
              </summary>
              <pre className="text-[11px] font-mono bg-surface-elev rounded-xs p-2 mt-1 overflow-x-auto">
                {JSON.stringify(changes.keepaSelection, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      {isBrand && changes && (
        <div className="text-sm text-fg-secondary space-y-1">
          {changes.brandName && (
            <p>
              <span className="text-fg-tertiary text-xs uppercase tracking-wider mr-2">Brand</span>
              <span className="font-medium text-fg-primary">{changes.brandName}</span>
              {changes.domain !== undefined && changes.domain !== 1 && (
                <span className="text-fg-tertiary text-xs ml-2">domain {changes.domain}</span>
              )}
            </p>
          )}
          <div className="flex items-center gap-3 flex-wrap text-xs text-fg-tertiary tabular-nums">
            {changes.reason && (
              <span>
                Reason: <span className="font-mono text-fg-secondary">{changes.reason}</span>
              </span>
            )}
            {changes.attempts !== undefined && (
              <span>
                Attempt <span className="font-medium text-fg-secondary">{changes.attempts}</span>
                {changes.terminal && <span className="text-negative ml-1">(terminal)</span>}
              </span>
            )}
            {changes.productCount !== undefined && (
              <span>
                <span className="font-medium text-fg-secondary">{changes.productCount}</span>{' '}
                products
              </span>
            )}
            {changes.asinsHydrated !== undefined && (
              <span>
                <span className="font-medium text-fg-secondary">{changes.asinsHydrated}</span>{' '}
                hydrated
              </span>
            )}
            {changes.totalReviewCount !== undefined && (
              <span>
                <span className="font-medium text-fg-secondary">
                  {changes.totalReviewCount.toLocaleString()}
                </span>{' '}
                reviews
              </span>
            )}
            {changes.sizeBucket && (
              <span>
                Size: <span className="font-mono text-fg-secondary">{changes.sizeBucket}</span>
              </span>
            )}
            {changes.tokensLeft !== undefined && (
              <span>
                Tokens: <span className="font-medium text-fg-secondary">{changes.tokensLeft}</span>
                {changes.floor !== undefined && (
                  <span className="text-fg-tertiary"> / floor {changes.floor}</span>
                )}
              </span>
            )}
            {changes.sourceRunId && (
              <span>
                Run:{' '}
                <span className="font-mono text-fg-secondary">
                  {changes.sourceRunId.slice(0, 8)}
                </span>
              </span>
            )}
          </div>
          {changes.keepaSelection && (
            <details className="mt-1.5">
              <summary className="text-xs text-fg-tertiary cursor-pointer hover:text-fg-secondary">
                Show Keepa /query selection
              </summary>
              <pre className="text-[11px] font-mono bg-surface-elev rounded-xs p-2 mt-1 overflow-x-auto">
                {JSON.stringify(changes.keepaSelection, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      {!isSearch && !isBrand && changes && (
        <div className="text-sm text-fg-secondary space-y-0.5">
          {changes.provider && (
            <p>
              <span className="text-fg-tertiary text-xs uppercase tracking-wider mr-2">
                Provider
              </span>
              {changes.provider}
              {changes.label !== undefined && changes.label && (
                <span className="text-fg-tertiary"> — {changes.label}</span>
              )}
            </p>
          )}
          {changes.ok !== undefined && (
            <p className="text-xs text-fg-tertiary">Result: {changes.ok ? 'ok' : 'invalid'}</p>
          )}
        </div>
      )}

      {row.errorMessage && (
        <p className="text-xs text-negative mt-2 font-mono">{row.errorMessage}</p>
      )}
    </Card>
  );
}
